<script lang="ts">import { cfgToThemeCdnPath } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { isMobile } from "@infinitro/global";
import { InfoCard } from "@infinitro/info-card";
import { Text } from "@infinitro/text";
import Swiper from "swiper";
import { Pagination } from "swiper/modules";
import "swiper/swiper-bundle.css";
import VipModernEyebrow from "./vip-modern-eyebrow.svelte";
const steps = [
	{
		label: Verbiage.vip_modern_step_01,
		description: Verbiage.vip_modern_rewards_are_glowing,
		image: cfgToThemeCdnPath("/about-vip/modern/claim-step-1.png"),
		imageMobile: cfgToThemeCdnPath("/about-vip/modern/claim-step-1-mobile.png")
	},
	{
		label: Verbiage.vip_modern_step_02,
		description: Verbiage.vip_modern_click_the_claim_button,
		image: cfgToThemeCdnPath("/about-vip/modern/claim-step-2.png"),
		imageMobile: cfgToThemeCdnPath("/about-vip/modern/claim-step-2-mobile.png")
	},
	{
		label: Verbiage.vip_modern_step_03,
		description: Verbiage.vip_modern_percentage_credited_to_balance_remaining_over_following_days,
		image: cfgToThemeCdnPath("/about-vip/modern/claim-step-3.png"),
		imageMobile: cfgToThemeCdnPath("/about-vip/modern/claim-step-3-mobile.png")
	}
];
let swiperContainer = $state();
$effect(() => {
	if (!$isMobile || !swiperContainer) return;
	const swiper = new Swiper(swiperContainer, {
		modules: [Pagination],
		loop: false,
		spaceBetween: 0,
		centeredSlides: true,
		slidesPerView: 1.25,
		slidesPerGroup: 1,
		watchSlidesProgress: true,
		pagination: {
			clickable: true,
			el: ".vip-claim-steps-pagination",
			type: "bullets",
			dynamicBullets: false
		}
	});
	return () => swiper.destroy(true, true);
});
</script>

{#snippet stepCard(step: (typeof steps)[number], index: number)}
	<InfoCard
		modernStyle
		modernGlowPosition="right"
		noPadding
		borderRadius="8px"
		customClass="vip-claim-steps-card vip-claim-steps-card-{index + 1}"
	>
		<div class="vip-claim-steps-card-inner">
			<Text type="body" bodySize="medium" bodyWeight="bold" className="vip-claim-steps-label">
				{$t(step.label)}
			</Text>
			<img
				class="vip-claim-steps-button-img"
				src={$isMobile ? step.imageMobile : step.image}
				alt={$t(Verbiage.illustration_alt, 'alts')}
			/>
			<Text type="body" bodySize="medium" textColor="white" className="vip-claim-steps-description">
				{$t(step.description)}
			</Text>
		</div>
	</InfoCard>
{/snippet}

<section class="vip-claim-steps">
	<VipModernEyebrow text={$t(Verbiage.vip_modern_how)} />

	<Text
		type="title"
		titleSize="h4"
		titleWeight="bold"
		textColor="white"
		textAlign={$isMobile ? 'start' : 'center'}
		className="vip-claim-steps-title"
	>
		{$t(Verbiage.vip_modern_claim_steps_title)}
	</Text>

	<Text
		type="body"
		bodySize="large"
		textColor="gray"
		textAlign={$isMobile ? 'start' : 'center'}
		className="vip-claim-steps-body"
	>
		{$t(Verbiage.vip_modern_claim_steps_body)}
	</Text>

	{#if $isMobile}
		<div class="vip-claim-steps-swiper" bind:this={swiperContainer}>
			<div class="swiper-wrapper">
				{#each steps as step, i (step.label)}
					<div class="swiper-slide">
						{@render stepCard(step, i)}
					</div>
				{/each}
			</div>
			<div class="vip-claim-steps-pagination"></div>
		</div>
	{:else}
		<div class="vip-claim-steps-grid">
			{#each steps as step, i (step.label)}
				{@render stepCard(step, i)}
			{/each}
		</div>
	{/if}
</section>

<style>
	.vip-claim-steps {
		display: flex;
		width: 100%;
		flex-direction: column;
		align-items: flex-start;
		gap: 1.25rem;
		text-align: left;
}

	:global(.vip-claim-steps-body) {
		max-width: 50rem;
	}

	:global(.vip-claim-steps-title) {
		font-size: 32px !important;
	}

	.vip-claim-steps-grid {
		margin-top: 1.5rem;
		display: flex;
		width: 100%;
		flex-direction: column;
		align-items: stretch;
		gap: 1rem;
}

	.vip-claim-steps-swiper {
		margin-top: 1.5rem;
		width: 100%;
}

	:global(.vip-claim-steps-swiper .swiper-wrapper) {
		align-items: stretch;
}

	:global(.vip-claim-steps-pagination) {
		margin-top: 1rem;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 0.5rem;
		position: relative !important;
}

	:global(.vip-claim-steps-pagination .swiper-pagination-bullet) {
		display: block;
		height: 0.375rem;
		width: 0.375rem;
		border-radius: 9999px;
		background: var(--color-neutral-700);
		opacity: 1;
		margin: 0 !important;
}

	:global(.vip-claim-steps-pagination .swiper-pagination-bullet-active) {
		background: var(--color-primary-500);
	}

	:global(.vip-claim-steps-card) {
		aspect-ratio: 1 / 1;
		height: 300px;
		width: auto;
		margin: 0 auto;
	}

	.vip-claim-steps-card-inner {
		position: relative;
		display: flex;
		height: 100%;
		flex-direction: column;
		justify-content: space-between;
		gap: 0.75rem;
		padding: 1rem;
		text-align: left;
}

	:global(.vip-claim-steps-label) {
		text-transform: uppercase;
		letter-spacing: 0.025em;
		color: var(--color-primary-300) !important;
}

	.vip-claim-steps-button-img {
		position: relative;
		height: auto;
		width: 100%;
		-o-object-fit: contain;
		   object-fit: contain;
}

	:global(.vip-claim-steps-description) {
		text-align: center !important;
	}

	@media (min-width: 1024px) {
		.vip-claim-steps {
				align-items: center;
				text-align: center;
		}

		:global(.vip-claim-steps-title) {
			font-size: 38px !important;
		}

		.vip-claim-steps-grid {
				margin-top: 2.5rem;
				flex-direction: row;
				align-items: flex-start;
				gap: 0.75rem;
		}

		:global(.vip-claim-steps-card-2) {
			margin-top: 2rem;
		}

		:global(.vip-claim-steps-card-3) {
			margin-top: 4rem;
		}

		:global(.vip-claim-steps-card) {
			aspect-ratio: auto;
			height: auto;
			width: 100%;
			margin: 0;
		}

		.vip-claim-steps-card-inner {
				justify-content: space-around;
			min-height: 160px;
			height: auto;
		}

		.vip-claim-steps-button-img {
				position: absolute;
				top: 0px;
				right: 0px;
				height: 6rem;
				width: auto;
				max-width: 280px;
		}

		:global(.vip-claim-steps-description) {
			text-align: left;
		}
	}
</style>
